/**
 * A prioridade da tarefa, na tela.
 *
 * ── A tarja é o PRAZO; a pastilha é a PRIORIDADE ──────────────────────────
 *
 * São duas urgências diferentes, e o erro fácil é pintar as duas. Era o que
 * esta tela fazia: "em atraso" e "Urgente" saíam como dois chips VERMELHOS,
 * lado a lado, na mesma linha — duas cores brigando pelo mesmo significado, e
 * a pessoa parando de ler as duas.
 *
 * O prazo fica com a cor porque é FATO — venceu ou não venceu — e porque o
 * servidor já aplicou o expediente da revenda antes de marcar. A prioridade
 * fica com a forma: uma pastilha com o nome escrito, porque ela é JUÍZO de
 * quem criou a tarefa, e juízo se lê, não se adivinha pela cor.
 *
 * ── Uma cor só, entre quatro degraus ──────────────────────────────────────
 *
 * Só `urgente` interrompe a varredura, e em laranja, que no sistema é ênfase e
 * não estado. `alta` fica neutra com o ponto; `normal` não mostra nada, porque
 * é o meio da escala e o meio é o silêncio; `baixa` perde até o ponto, porque
 * ela é a AUSÊNCIA de urgência e um sinal ali diria que há algo a notar.
 *
 * Num dia com dez tarefas, três níveis coloridos fazem a lista inteira piscar.
 *
 * ── Por que ela NÃO reordena a lista ──────────────────────────────────────
 *
 * O eixo da tela é o prazo: os grupos são Atrasadas · Hoje · Próximos dias. A
 * prioridade ordena DENTRO do grupo (ver `grupos.ts`), e só ali — uma tarefa
 * urgente para semana que vem não vem antes de uma normal para hoje.
 */

import type { PrioridadeDaTarefa } from '@cliqcrm/contracts';

export interface DegrauDePrioridade {
  readonly valor: PrioridadeDaTarefa;
  readonly nome: string;
}

/** Na ordem em que se lê uma escala, do menor para o maior. */
export const PRIORIDADES: readonly DegrauDePrioridade[] = [
  { valor: 'baixa', nome: 'Baixa' },
  { valor: 'normal', nome: 'Normal' },
  { valor: 'alta', nome: 'Alta' },
  { valor: 'urgente', nome: 'Urgente' },
];

/**
 * A pastilha da linha — ou nada.
 *
 * `normal` não ganha pastilha, e é a decisão que faz as outras três valerem
 * alguma coisa: uma lista em que toda linha tem etiqueta é uma lista em que
 * nenhuma chama atenção.
 *
 * A classe sai daqui em vez de o componente decidir, para que a folha da tela
 * continue sendo quem sabe qual degrau tem cor — se amanhã `alta` precisar de
 * ênfase, muda o CSS e não o TypeScript.
 */
export function pastilhaDePrioridade(
  prioridade: PrioridadeDaTarefa,
): { nome: string; classe: string } | null {
  if (prioridade === 'normal') return null;

  const nome = PRIORIDADES.find((d) => d.valor === prioridade)?.nome ?? prioridade;
  return {
    nome,
    classe:
      prioridade === 'urgente' || prioridade === 'baixa'
        ? `tar__prioridade tar__prioridade--${prioridade}`
        : 'tar__prioridade',
  };
}
